// Port of TrailData/Import/GeoJSONImporter.swift.

import { isValidCoordinate, radiansToDegrees } from '../core/geo';
import type { DataProvenance, EpochMs, GeoCoordinate, GeoPolygon, RoutePoint } from '../core/models';
import { isPlausibleLV95, wgs84FromLV95 } from '../core/swiss';
import { TrailDataError, errorMessage } from '../data/errors';
import { ImportAssembler, type ImportFeature, makeImportFeature, normalizedProperties } from './assembler';
import type { ImportedGeoData } from './importedGeoData';
import { trimWhitespace } from './text';

type Position = number[];

/** Raw (unprojected) geometry. */
export type RawGeometry =
  | { kind: 'points'; positions: Position[] }
  | { kind: 'lines'; lines: Position[][] }
  | { kind: 'polygons'; polygons: Position[][][] }
  | { kind: 'collection'; members: RawGeometry[] };

interface RawFeature {
  sourceID?: string;
  properties: Record<string, string>;
  geometry: RawGeometry;
}

type JSONObject = Record<string, unknown>;

const isObject = (v: unknown): v is JSONObject => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Foundation bridges JSON numbers and booleans to `NSNumber` (true → 1). */
function nsNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  return undefined;
}

const geometryTypes = new Set([
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
  'GeometryCollection',
]);

/**
 * Imports GeoJSON (RFC 7946) — a `FeatureCollection`, a single `Feature` or a bare geometry.
 *
 * Geometry mapping:
 * - `Point` / `MultiPoint` → `PointOfInterest` when the properties map to a `POICategory` (`trailbivvy:category`,
 *   `category`, or OSM tags such as `tourism=alpine_hut`); otherwise an imported `Waypoint`, with a warning.
 * - `LineString` / `MultiLineString` → one stage of a single imported `Trail` (parts of a multi-line joined).
 * - `Polygon` / `MultiPolygon` → `ProtectedArea` (`kind`, `overnight_rule`, `name`, `confidence`,
 *   `season_start`/`season_end`, `access_rule`, `country` properties). `overnightRule` is `unknown` and confidence
 *   `low` unless stated. A polygon whose properties describe a facility (e.g. `tourism=camp_site`) and nothing
 *   protected-area-like becomes a POI at its centroid instead.
 * - `GeometryCollection` members are handled individually with the feature's properties.
 *
 * Coordinate reference systems: WGS84 longitude/latitude by default. Swiss projected coordinates are detected when any
 * |x| > 1000 (LV95 / EPSG:2056, and LV03 / EPSG:21781 when easting < 1,000,000) and converted with src/core/swiss.ts.
 * A legacy `crs` member naming EPSG:2056, EPSG:21781, EPSG:4326, CRS84 or EPSG:3857 (Web Mercator) is honoured;
 * other systems are rejected.
 */
export class GeoJSONImporter {
  /**
   * Imports GeoJSON text (or its UTF-8 bytes).
   * @param sourceName File name, used for provenance, ids and fallback names.
   * @param provenance Provenance for all records; defaults to `userImportProvenance`.
   * @throws TrailDataError `decoding` (invalid JSON or structure), `unsupportedFormat` (not GeoJSON, unsupported CRS),
   *   `noGeometry` (nothing usable).
   */
  importData(data: string | Uint8Array, sourceName: string, importedAt: EpochMs, provenance?: DataProvenance): ImportedGeoData {
    const text = typeof data === 'string' ? data : new TextDecoder().decode(data);
    let root: unknown;
    try {
      root = JSON.parse(text);
    } catch (e) {
      throw TrailDataError.decoding(sourceName, `invalid JSON: ${errorMessage(e)}`);
    }
    if (!isObject(root) || typeof root.type !== 'string') {
      throw TrailDataError.unsupportedFormat("JSON without a GeoJSON 'type' member");
    }
    const type = root.type;

    const assembler = new ImportAssembler(data, sourceName, importedAt, provenance);
    if (typeof root.name === 'string' && trimWhitespace(root.name) !== '') {
      assembler.collectionName = trimWhitespace(root.name);
    }

    // 1. Parse the structure into raw (unprojected) features.
    const rawFeatures: RawFeature[] = [];
    if (type === 'FeatureCollection') {
      if (!Array.isArray(root.features)) {
        throw TrailDataError.decoding(sourceName, "FeatureCollection without a 'features' array");
      }
      root.features.forEach((element: unknown, index: number) => {
        if (!isObject(element) || element.type !== 'Feature') {
          assembler.warn(`feature ${index + 1}: not a GeoJSON Feature; skipped`);
          return;
        }
        const raw = this.parseFeature(element, `feature ${index + 1}`, assembler);
        if (raw) rawFeatures.push(raw);
      });
    } else if (type === 'Feature') {
      const raw = this.parseFeature(root, 'feature 1', assembler);
      if (raw) rawFeatures.push(raw);
    } else if (geometryTypes.has(type)) {
      const geometry = this.parseGeometry(root, 'geometry', assembler);
      if (!geometry) throw TrailDataError.noGeometry();
      rawFeatures.push({ properties: {}, geometry });
    } else {
      throw TrailDataError.unsupportedFormat(`GeoJSON type '${type}'`);
    }

    // 2. Choose the coordinate system for the whole document, then project.
    const system = coordinateSystemFromCRS(root.crs) ?? detectCoordinateSystem(rawFeatures.map((f) => f.geometry));
    if (system === 'swissProjected') {
      assembler.warn('coordinates interpreted as Swiss LV95 (EPSG:2056) / LV03 (EPSG:21781) and converted to WGS84');
    }
    const converter = new PositionConverter(system);
    rawFeatures.forEach((raw, index) => {
      const feature = makeImportFeature(raw.sourceID, normalizedProperties(raw.properties));
      converter.collect(raw.geometry, feature);
      if (converter.invalidPositions > 0) {
        assembler.warn(`feature ${raw.sourceID ?? String(index + 1)}: ${converter.invalidPositions} invalid position(s) skipped`);
        converter.invalidPositions = 0;
      }
      assembler.add(feature);
    });
    if (converter.implausibleSwissPositions > 0) {
      assembler.warn(`${converter.implausibleSwissPositions} Swiss position(s) outside the LV95 plausibility range`);
    }
    return assembler.finish();
  }

  // MARK: Raw structure

  private parseFeature(feature: JSONObject, label: string, assembler: ImportAssembler): RawFeature | undefined {
    if (!isObject(feature.geometry)) {
      assembler.warn(`${label}: no geometry; skipped`);
      return undefined;
    }
    const geometry = this.parseGeometry(feature.geometry, label, assembler);
    if (!geometry) return undefined;
    let sourceID: string | undefined;
    if (typeof feature.id === 'string') sourceID = feature.id;
    else if (typeof feature.id === 'number' || typeof feature.id === 'boolean') sourceID = String(nsNumber(feature.id));
    const raw: RawFeature = { properties: flattenProperties(feature.properties), geometry };
    if (sourceID !== undefined) raw.sourceID = sourceID;
    return raw;
  }

  private parseGeometry(object: JSONObject, label: string, assembler: ImportAssembler): RawGeometry | undefined {
    if (typeof object.type !== 'string') {
      assembler.warn(`${label}: geometry without type; skipped`);
      return undefined;
    }
    const type = object.type;
    const coordinates = object.coordinates;
    let parsed: RawGeometry | undefined;
    switch (type) {
      case 'Point': {
        const p = position(coordinates);
        parsed = p ? { kind: 'points', positions: [p] } : undefined;
        break;
      }
      case 'MultiPoint': {
        const ps = positions(coordinates);
        parsed = ps ? { kind: 'points', positions: ps } : undefined;
        break;
      }
      case 'LineString': {
        const ps = positions(coordinates);
        parsed = ps ? { kind: 'lines', lines: [ps] } : undefined;
        break;
      }
      case 'MultiLineString': {
        const ls = nested(coordinates, positions);
        parsed = ls ? { kind: 'lines', lines: ls } : undefined;
        break;
      }
      case 'Polygon': {
        const rings = nested(coordinates, positions);
        parsed = rings ? { kind: 'polygons', polygons: [rings] } : undefined;
        break;
      }
      case 'MultiPolygon': {
        const polygons = nested(coordinates, (v) => nested(v, positions));
        parsed = polygons ? { kind: 'polygons', polygons } : undefined;
        break;
      }
      case 'GeometryCollection': {
        const members: RawGeometry[] = [];
        for (const element of Array.isArray(object.geometries) ? object.geometries : []) {
          if (!isObject(element)) continue;
          const member = this.parseGeometry(element, label, assembler);
          if (member) members.push(member);
        }
        parsed = members.length === 0 ? undefined : { kind: 'collection', members };
        break;
      }
      default:
        assembler.warn(`${label}: unsupported geometry type '${type}'; skipped`);
        return undefined;
    }
    if (!parsed) assembler.warn(`${label}: malformed ${type} coordinates; skipped`);
    return parsed;
  }
}

function position(value: unknown): Position | undefined {
  if (!Array.isArray(value) || value.length < 2) return undefined;
  const out: Position = [];
  for (const element of value.slice(0, 3)) {
    const n = nsNumber(element);
    if (n === undefined) return undefined;
    out.push(n);
  }
  return out;
}

function positions(value: unknown): Position[] | undefined {
  return nested(value, position);
}

function nested<T>(value: unknown, parse: (v: unknown) => T | undefined): T[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: T[] = [];
  for (const element of value) {
    const parsed = parse(element);
    if (parsed === undefined) return undefined;
    out.push(parsed);
  }
  return out;
}

function scalarString(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return String(value);
  return undefined;
}

/** Flattens a properties object into strings; a nested `tags` object (Overpass exports) is merged in. */
export function flattenProperties(value: unknown): Record<string, string> {
  if (!isObject(value)) return {};
  const out: Record<string, string> = Object.create(null) as Record<string, string>;
  for (const [key, raw] of Object.entries(value)) {
    const s = scalarString(raw);
    if (s !== undefined) out[key] = s;
  }
  if (isObject(value.tags)) {
    for (const [key, raw] of Object.entries(value.tags)) {
      if (Object.hasOwn(out, key)) continue;
      const s = scalarString(raw);
      if (s !== undefined) out[key] = s;
    }
  }
  return out;
}

// MARK: Coordinate systems

export type CoordinateSystem = 'wgs84' | 'swissProjected' | 'webMercator';

/** Reads the legacy (GeoJSON 2008) `crs` member; undefined when absent. Throws `unsupportedFormat` otherwise. */
export function coordinateSystemFromCRS(crs: unknown): CoordinateSystem | undefined {
  if (!isObject(crs)) return undefined;
  const name = isObject(crs.properties) ? crs.properties.name : undefined;
  if (typeof name !== 'string') throw TrailDataError.unsupportedFormat("unreadable 'crs' member");
  const upper = name.toUpperCase();
  if (upper.includes('CRS84')) return 'wgs84';
  const groups = upper.match(/\d+/g);
  const code = groups ? groups[groups.length - 1] : '';
  switch (code) {
    case '4326':
      return 'wgs84';
    case '2056':
    case '21781':
      return 'swissProjected';
    case '3857':
    case '900913':
    case '102100':
    case '102113':
      return 'webMercator';
    default:
      throw TrailDataError.unsupportedFormat(`coordinate reference system '${name}'`);
  }
}

/** Swiss projected coordinates when any |x| exceeds 1000 (impossible for longitudes), else WGS84. */
export function detectCoordinateSystem(geometries: readonly RawGeometry[]): CoordinateSystem {
  const projected = (g: RawGeometry): boolean => {
    switch (g.kind) {
      case 'points':
        return g.positions.some((p) => Math.abs(p[0]) > 1000);
      case 'lines':
        return g.lines.some((l) => l.some((p) => Math.abs(p[0]) > 1000));
      case 'polygons':
        return g.polygons.some((rings) => rings.some((r) => r.some((p) => Math.abs(p[0]) > 1000)));
      case 'collection':
        return g.members.some(projected);
    }
  };
  return geometries.some(projected) ? 'swissProjected' : 'wgs84';
}

/** Converts raw positions to validated `RoutePoint`s, counting what had to be skipped. */
class PositionConverter {
  invalidPositions = 0;
  implausibleSwissPositions = 0;

  constructor(readonly system: CoordinateSystem) {}

  point(p: Position): RoutePoint | undefined {
    const x = p[0];
    const y = p[1];
    const z = p.length > 2 && Number.isFinite(p[2]) ? p[2] : undefined;
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      this.invalidPositions += 1;
      return undefined;
    }
    let coordinate: GeoCoordinate;
    switch (this.system) {
      case 'wgs84':
        coordinate = { latitude: y, longitude: x };
        break;
      case 'swissProjected': {
        // LV03 easting/northing are ~2,000,000 / 1,000,000 smaller than LV95.
        const isLV03 = x < 1_000_000 && y < 1_000_000;
        const lv95 = { easting: isLV03 ? x + 2_000_000 : x, northing: isLV03 ? y + 1_000_000 : y };
        if (!isPlausibleLV95(lv95)) this.implausibleSwissPositions += 1;
        coordinate = wgs84FromLV95(lv95);
        break;
      }
      case 'webMercator': {
        const r = 6_378_137;
        coordinate = {
          latitude: radiansToDegrees(2 * Math.atan(Math.exp(y / r)) - Math.PI / 2),
          longitude: radiansToDegrees(x / r),
        };
        break;
      }
    }
    if (!isValidCoordinate(coordinate)) {
      this.invalidPositions += 1;
      return undefined;
    }
    return z === undefined ? { coordinate } : { coordinate, elevation: z };
  }

  collect(geometry: RawGeometry, feature: ImportFeature): void {
    switch (geometry.kind) {
      case 'points':
        for (const p of geometry.positions) {
          const point = this.point(p);
          if (point) feature.points.push(point);
        }
        break;
      case 'lines':
        for (const line of geometry.lines) feature.lines.push(this.convertAll(line));
        break;
      case 'polygons':
        for (const rings of geometry.polygons) {
          const converted = rings.map((ring) => this.convertAll(ring).map((p) => p.coordinate));
          const exterior = converted[0];
          if (!exterior) continue;
          const polygon: GeoPolygon = { exterior, holes: converted.slice(1) };
          feature.polygons.push(polygon);
        }
        break;
      case 'collection':
        for (const member of geometry.members) this.collect(member, feature);
        break;
    }
  }

  private convertAll(positions: Position[]): RoutePoint[] {
    const out: RoutePoint[] = [];
    for (const p of positions) {
      const point = this.point(p);
      if (point) out.push(point);
    }
    return out;
  }
}
